<template>
  <div class="realogram-page">
    <div class="page-header mb-4">
      <div class="header-title">
        <div class="header-icon-wrap">
          <img src="@/assets/img/camera.png" class="header-icon" />
        </div>
        <span class="page-title-bar">{{ $t("Realogram") }}</span>
      </div>
    </div>

    <!-- Search Panel -->
    <v-card class="search-card mb-4" elevation="0">
      <v-card-text class="pa-4">
        <v-row no-gutters class="d-flex align-end">
          <v-col class="pr-2 mb-2 mb-md-0">
            <v-select
              v-model="searchBy.floor"
              :items="floorItems"
              :label="$t('Floor')"
              :placeholder="$t('Select Floor')"
              clearable
              outlined
              dense
              hide-details
              class="form-select"
            ></v-select>
          </v-col>
          <v-col class="pr-2 mb-2 mb-md-0">
            <v-select
              v-model="searchBy.section"
              :items="floorMap[searchBy.floor]"
              :label="$t('Section')"
              :placeholder="$t('Select Section')"
              :disabled="!searchBy.floor"
              clearable
              outlined
              dense
              hide-details
              class="form-select"
            ></v-select>
          </v-col>
          <v-col class="pr-2 mb-2 mb-md-0">
            <v-select
              v-model="searchBy.error"
              :items="errorItems"
              :label="$t('Error')"
              :placeholder="$t('Select Error')"
              item-text="label"
              item-value="value"
              clearable
              outlined
              dense
              hide-details
              class="form-select"
            ></v-select>
          </v-col>
          <v-col class="pr-2 mb-2 mb-md-0" style="max-width: 190px; flex: 0 0 190px;">
            <v-btn @click="search" class="btn search-btn mr-2">
              {{ $t("Search") }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row no-gutters class="mt-4 d-flex align-end">
          <v-col class="pr-2 mb-2 mb-md-0">
            <v-select
              v-model="searchBy.status"
              :items="statusItems"
              :label="$t('Status')"
              :placeholder="$t('Select Status')"
              item-text="text"
              item-value="value"
              clearable
              outlined
              dense
              hide-details
              class="form-select"
            ></v-select>
          </v-col>
          <v-col class="pr-2 mb-2 mb-md-0">
            <v-select
              v-model="searchBy.selectBy"
              :items="searchByItems"
              :label="$t('Select By')"
              :placeholder="$t('Select By')"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              class="form-select"
            ></v-select>
          </v-col>
          <v-col v-if="searchBy.selectBy" class="pr-2 mb-2 mb-md-0">
            <v-text-field
              v-model="searchBy.shelf"
              hide-details
              :placeholder="
                searchBy.selectBy === 'SHELF ID'
                  ? $t('Input Shelf Id')
                  : $t('Input Shelf Name')
              "
              :label="
                searchBy.selectBy === 'SHELF ID'
                  ? $t('Shelf Id')
                  : $t('Shelf Name')
              "
              outlined
              dense
              clearable
              class="form-select"
            />
          </v-col>
          <v-col
            class="pr-2 mb-2 mb-md-0"
            style="max-width: 190px; flex: 0 0 190px;"
          >
            <v-btn @click="clear" class="btn search-btn">
              {{ $t("Clear") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col cols="3" class="pr-0">
        <v-card class="location-card" elevation="0">
          <div class="location-header">
            <v-icon class="mr-2" color="white" small
              >mdi-map-marker-multiple</v-icon
            >
            <span>{{ $t("Location") }}</span>
          </div>
          <div class="location-body">
            <div v-if="!treeList.length" class="no-data-wrap">
              <v-icon color="#c5c5c5" size="40">mdi-folder-open-outline</v-icon>
              <p class="no-data-text mt-2">{{ $t("No data available") }}</p>
            </div>
            <v-treeview
              v-else
              :active.sync="active"
              :open="initiallyOpen"
              :items="treeList"
              item-key="_key"
              item-text="label"
              item-children="children"
              activatable
              open-on-click
              return-object
              @update:active="handleTree"
              active-class="tree-active"
              class="custom-treeview"
            />
          </div>
        </v-card>
      </v-col>

      <v-col cols="9">
        <v-card class="table-card" elevation="0">
          <div class="table-header-bar px-4 py-3 d-flex align-center">
            <v-icon color="#001e38" class="mr-2">mdi-table-large</v-icon>
            <span class="table-title">{{ $t("Shelf Overview") }}</span>
          </div>
          <v-data-table
            :headers="shelfHeaders"
            :items="shelfItems"
            :hide-default-footer="true"
            class="tbl-type01 improved-table"
          >
            <template #[`item.no`]="{ item }">
              <span class="row-number">{{ shelfItems.indexOf(item) + 1 }}</span>
            </template>
            <template #[`item.shelfId`]="{ item }">
              <span class="shelf-id-text">{{ item.shelfId }}</span>
            </template>
            <template #[`item.eslMis`]="{ item }">
              {{ item.errorType.eslMis }}
            </template>
            <template #[`item.eslUn`]="{ item }">
              {{ item.errorType.eslUn }}
            </template>
            <template #[`item.status`]="{ item }">
              <v-tooltip
                v-if="item.resultMessage && item.resultMessage !== '-'"
                top
                max-width="300"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-chip
                      outlined
                      small
                      :color="item.status === 'SUCCESS' ? '#2ABC4E' : '#EA3F65'"
                      class="status-chip"
                      >{{
                        item.status === "SUCCESS" ? "Success" : "Failure"
                      }}</v-chip
                    >
                  </span>
                </template>
                <span>{{ item.resultMessage }}</span>
              </v-tooltip>
              <span v-else>
                <v-chip
                  outlined
                  small
                  class="status-chip"
                  :color="item.status === 'SUCCESS' ? '#2ABC4E' : '#EA3F65'"
                  >{{
                    item.status === "SUCCESS" ? "Success" : "Failure"
                  }}</v-chip
                >
              </span>
            </template>
            <template #[`item.setting`]="{ item }">
              <v-btn
                icon
                small
                @click="handleShelf(item)"
                :disabled="!!btnDisabledDetails"
                class="action-btn"
                title="Settings"
              >
                <img src="@/assets/img/ico-setting.png" alt="" />
              </v-btn>
            </template>
            <template #[`item.details`]="{ item }">
              <v-btn
                icon
                small
                :disabled="!!btnDisabledDetails"
                @click="handleRealogram(item)"
                class="action-btn"
                title="View Details"
              >
                <v-icon small>mdi-file-eye-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <ShelfDetailsModal
      v-model="showShelfModal"
      :shelfItem="selectedShelfItem"
    />
    <RealogramDetailModal
      v-model="showRealogramModal"
      :shelfItem="selectedShelfItem"
    />
  </div>
</template>
<script>
import RealogramDetailModal from './model/RealogramDetailModal.vue'
import ShelfDetailsModal from './model/ShelfDetailsModal.vue'
import codes from '@/plugins/codes'
import { mapGetters } from 'vuex'
import commons from '@/plugins/commons'
import EventBus from '@/plugins/eventBus.js'

export default {
  components: {
    ShelfDetailsModal,
    RealogramDetailModal
  },
  data () {
    return {
      showShelfModal: false,
      showRealogramModal: false,
      searchBy: {
        floor: null,
        section: null,
        error: null,
        status: null,
        selectBy: 'SHELF ID',
        shelf: null
      },
      tree: [],
      active: [],
      lastActiveNode: [],
      shelfList: [],
      treeShelfList: [],
      shelfItems: [],
      selected: [],
      shelfHeaders: [
        { text: '#', align: 'center', sortable: false, value: 'no' },
        {
          text: this.$t('Shelf ID'),
          align: 'center',
          sortable: false,
          value: 'shelfId'
        },
        {
          text: this.$t('Product'),
          align: 'center',
          sortable: false,
          value: 'productCount'
        },
        {
          text: this.$t('ESL Misplaced'),
          align: 'center',
          sortable: false,
          value: 'eslMis'
        },
        {
          text: this.$t('ESL Undetected'),
          align: 'center',
          sortable: false,
          value: 'eslUn'
        },
        {
          text: this.$t('Status'),
          align: 'center',
          sortable: false,
          value: 'status'
        },
        {
          text: this.$t('Last Capture Time'),
          align: 'center',
          sortable: false,
          value: 'lastCaptureTime'
        },
        {
          text: this.$t('Settings'),
          align: 'center',
          sortable: false,
          value: 'setting'
        },
        {
          text: this.$t('Details'),
          align: 'center',
          sortable: false,
          value: 'details'
        }
      ],
      selectedShelfItem: [],
      floorItems: [],
      floorMap: {},
      errorItems: [
        {
          label: this.$t('ESL UN'),
          value: 'eslUn'
        },
        {
          label: this.$t('ESL MIS'),
          value: 'eslMis'
        }
      ],
      statusItems: [
        { text: this.$t('SUCCESS'), value: 'SUCCESS' },
        { text: this.$t('FAILURE'), value: 'FAILURE' }
      ],
      searchByItems: [
        { text: this.$t('SHELF ID'), value: 'SHELF ID' },
        { text: this.$t('SHELF NAME'), value: 'SHELF NAME' }
      ],
      btnDisabledDetails: null
    }
  },
  watch: {
    '$route.params.error' () {
      this.applyRouteErrorFilter()
      this.search()
    },
    store: {
      handler () {
        if (!this.store.code) {
          EventBus.$emit('openSelectedStores')
          this.floorItems = []
          this.floorMap = {}
          this.tree = []
          this.active = []
          this.lastActiveNode = []
          this.shelfList = []
          this.treeShelfList = []
          this.shelfItems = []
          this.selected = []
          this.selectedShelfItem = []
          this.searchBy = { floor: null, section: null, error: null, status: null, selectBy: 'SHELF ID', shelf: null }
          return
        }
        this.floorItems = []
        this.floorMap = {}
        this.applyRouteErrorFilter()
        this.loadTreeData()
        this.getDepDetails()
      }
    }
  },
  computed: {
    ...mapGetters({
      store: 'dataStore/GET_SELECTED_STORE'
    }),
    treeList () {
      return this.treeShelfList.map(floor => {
        return {
          ...floor,
          label: floor.floor,
          _key: floor.floor,
          children: floor.sections.map(section => {
            return {
              ...section,
              label: section.section,
              _key: section.section,
              children: section.shelfList.map(shelf => {
                return {
                  ...shelf,
                  label: shelf.shelfId,
                  _key: shelf.shelfId
                }
              })
            }
          })
        }
      })
    },
    initiallyOpen () {
      return [
        this.treeList[0],
        this.treeList[0]?.children[0],
        this.treeList[0]?.children[0]?.children[0]
      ].filter(Boolean)
    }
  },
  methods: {
    applyRouteErrorFilter () {
      const error = this.$route.params.error
      const hasError = this.errorItems.some(item => item.value === error)
      this.searchBy.error = hasError ? error : null
    },
    search () {
      this.loadTreeData(
        this.searchBy.section,
        this.searchBy.floor,
        this.searchBy.status,
        this.searchBy.error,
        this.searchBy.shelf
      )
    },
    clear () {
      this.searchBy = {
        floor: null,
        section: null,
        error: null,
        status: null,
        selectBy: 'SHELF ID',
        shelf: null
      }
      this.loadTreeData()
    },
    handleShelf (item) {
      this.showShelfModal = true
      this.selectedShelfItem = item
    },
    handleRealogram (item) {
      this.showRealogramModal = true
      this.selectedShelfItem = item
    },
    loadTreeData (
      section = undefined,
      floor = undefined,
      status = undefined,
      errorType = undefined,
      shelfName = undefined
    ) {
      const params = {
        company: this.$store.state.auth.user.company,
        store: this.store.code,
        section: section,
        floor: floor,
        status,
        errorType
      }
      if (this.searchBy.selectBy === 'SHELF ID') {
        params.shelfName = shelfName
      } else {
        params.shelfGroup = shelfName
      }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.getShelfOperation.method,
          codes.requests.getShelfOperation.url,
          config
        )
        .then(res => {
          if (res.status !== 204) {
            this.treeShelfList = res.data.linkedShelfOpStatusInfoList
            const firstFloor = this.treeList?.[0]
            const firstSection = firstFloor?.children?.[0]
            const firstShelf = firstSection?.children?.[0]
            if (firstShelf) {
              this.active = [firstShelf]
              this.lastActiveNode = [firstShelf]
            }
            this.getLinkedShelfOp(
              firstSection?.section,
              firstFloor?.floor,
              this.searchBy.status || undefined,
              this.searchBy.error || undefined,
              this.searchBy.shelf || undefined
            )
          } else {
            this.treeShelfList = []
            this.shelfItems = []
          }
        })
        .catch(() => {
          this.treeShelfList = []
          this.shelfItems = []
        })
    },
    getLinkedShelfOp (
      section = undefined,
      floor = undefined,
      status = undefined,
      errorType = undefined,
      shelfName = undefined
    ) {
      const params = {
        company: this.$store.state.auth.user.company,
        store: this.store.code,
        section: section,
        floor: floor,
        status,
        errorType
      }
      if (this.searchBy.selectBy === 'SHELF ID') {
        params.shelfName = shelfName
      } else {
        params.shelfGroup = shelfName
      }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.getShelfOperation.method,
          codes.requests.getShelfOperation.url,
          config
        )
        .then(res => {
          if (res.status !== 204) {
            this.shelfList = res.data.linkedShelfOpStatusInfoList
            this.shelfItems = this.shelfList.flatMap(f =>
              f.sections.flatMap(s => s.shelfList.map(shelf => ({ ...shelf, lastCaptureTime: commons.convertDate(shelf.lastCaptureTime) })))
            )
          } else {
            this.shelfItems = []
          }
        })
        .catch(() => {
          this.shelfItems = []
        })
    },
    getDepDetails () {
      const params = {
        company: this.$store.state.auth.user.company,
        store: this.store.code
      }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.getDepDetail.method,
          codes.requests.getDepDetail.url,
          config
        )
        .then(res => {
          if (res.status !== 204) {
            res.data.linkedShelvesDeptList.forEach(data => {
              this.floorItems.push(data.floor)
              this.floorMap[data.floor] = data.section
            })
          } else {
            this.floorItems = []
            this.flatMap = {}
          }
        })
    },
    handleTree (item) {
      if (!item || !item.length) {
        this.$nextTick(() => {
          this.active = [...this.lastActiveNode]
        })
        return
      }
      this.lastActiveNode = [...item]
      this.getLinkedShelfOp(item[0].section, item[0].floor, this.searchBy.status || undefined, this.searchBy.error || undefined, this.searchBy.shelf || undefined)
    },
    statusStyle (status) {
      if (status === 'SUCCESS') return 'txt-stable'
      if (status === 'PROCESSING') return 'txt-caution'
      if (status === 'FAILURE') return 'txt-danger'
      return ''
    }
  },
  mounted () {
    this.$store.dispatch('auth/getDisabledBtn', '14501').then(flag => {
      this.btnDisabledDetails = flag
    })
    this.applyRouteErrorFilter()
    this.loadTreeData()
    this.getDepDetails()
  }
}
</script>

<style scoped>
.realogram-page {
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon-wrap {
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  width: 28px;
  height: 28px;
  filter: brightness(10);
}

.header-text {
  font-size: 22px;
  font-weight: 700;
  color: #001e38;
  letter-spacing: 0.3px;
}

.search-card {
  border: 1px solid #e0e4ea !important;
  border-radius: 12px !important;
  background: #ffffff !important;
}

.search-btn {
  height: 40px !important;
  width: 100%;
  max-width: 190px;
}

.search-btn::v-deep.v-btn.btn .v-btn__content {
  line-height: 40px;
}

.location-card {
  border: 1px solid #e0e4ea !important;
  border-radius: 10px !important;
  overflow: hidden;
  background: #ffffff !important;
}

.location-header {
  background: linear-gradient(135deg, #001e38, #003366);
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  letter-spacing: 0.4px;
}

.location-body {
  max-height: 560px;
  overflow-y: auto;
  padding: 8px 4px;
}

.no-data-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
}

.no-data-text {
  font-size: 13px;
  color: #9aa0ac;
  margin: 0;
}

::v-deep .custom-treeview .v-treeview-node__label {
  font-size: 13px;
  font-weight: 500;
  color: #2c3e50;
}

::v-deep .custom-treeview .v-treeview-node__root:hover {
  background: #f0f4f8;
  border-radius: 6px;
}

::v-deep .tree-active {
  background-color: #001e38 !important;
  color: white !important;
  border-radius: 6px;
}

::v-deep .tree-active .v-treeview-node__label {
  color: #ffffff !important;
  font-weight: 600;
}

.table-card {
  border: 1px solid #e0e4ea !important;
  border-radius: 10px !important;
  overflow: hidden;
  background: #ffffff !important;
}

.table-header-bar {
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
}

.table-title {
  font-size: 14px;
  font-weight: 700;
  color: #001e38;
  letter-spacing: 0.3px;
}

::v-deep .improved-table thead th {
  background-color: #f0f3f7 !important;
  color: #001e38 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 2px solid #dde2ea !important;
}

::v-deep .improved-table tbody tr:hover {
  background: #f7f9fb !important;
}

::v-deep .improved-table tbody tr {
  border-bottom: 1px solid #f0f3f7;
}

.row-number {
  color: #9aa0ac;
  font-size: 12px;
  font-weight: 500;
}

.shelf-id-text {
  font-weight: 600;
  color: #001e38;
  font-size: 13px;
}

.status-chip {
  text-transform: capitalize;
}

.action-btn {
  transition: transform 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
  background: #f0f3f7 !important;
}

::v-deep ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep ::-webkit-scrollbar-track {
  background: #f4f6f9;
  border-radius: 4px;
}

::v-deep ::-webkit-scrollbar-thumb {
  background: #c5cdd6;
  border-radius: 4px;
}

::v-deep ::-webkit-scrollbar-thumb:hover {
  background: #9aa0ac;
}
</style>
<style>
.v-tooltip-resize {
  width: 200px;
  word-break: break-word;
}

.v-tooltip__content {
  background: #001e38;
  color: #fff;
}

.v-tooltip__content:after {
  content: "";
  display: block;
  width: 0;
  height: 0;
  position: absolute;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-top: 15px solid #001e38;
  left: 50%;
  transform: translateX(-50%);
  bottom: -10px;
}
</style>
